import { useEffect, useState } from "react";
import { Box, HStack, Text, Spinner } from "@chakra-ui/react";
import { HiLightBulb, HiCheckCircle, HiExclamationTriangle, HiXMark } from "react-icons/hi2";
import { useFormik, validateYupSchema, yupToFormErrors } from "formik";
import { toaster } from "@/components/ui/toaster";
import { extractBusinessErrorMessage } from "@/services/apiErrorMessage";
import { createEmptyCampaign, campaignValidationSchema, stepHasErrors } from "@/models/campaign";
import { stepCircleProps, stepAlertBorderColor, stepLabelColor } from "@/components/jobOffer/ui";
import Step1Campaign from "./steps/Step1Campaign";
import Step2LeadSource from "./steps/Step2LeadSource";
import Step3Products from "./steps/Step3Products";
import Step4ConversionGoal from "./steps/Step4ConversionGoal";
import Step5ChannelsAndRules from "./steps/Step5ChannelsAndRules";
import Step6AgentPersonalization from "./steps/Step6AgentPersonalization";
import Step7KnowledgeQualifying from "./steps/Step7KnowledgeQualifying";
import Step8FollowUpOutput from "./steps/Step8FollowUpOutput";

// Steps 1-5 are what a campaign actually needs to run (see campaignValidationSchema) --
// "Zapisz i aktywuj" becomes available right after step 5, not only after step 8.
// Steps 6-8 are pure refinement: better personas, more knowledge, extra automation/output.
const CORE_STEP_COUNT = 5;

const STEPS = [
    { id: 1, title: "Kampania", short: "Kampania", tier: "core", sections: ["Podstawowe informacje"] },
    { id: 2, title: "Źródło leadów", short: "Źródło", tier: "core", sections: ["Powiadomienia i źródło", "Mapowanie pól", "Kolumna decyzyjna i statusy"] },
    { id: 3, title: "Produkty i usługi", short: "Produkty", tier: "core", sections: ["Lista produktów"] },
    { id: 4, title: "Cel konwersji", short: "Cel", tier: "core", sections: ["Cele kampanii"] },
    { id: 5, title: "Kanał i reguły statusów", short: "Kanał", tier: "core", sections: ["Kanały komunikacji", "Reguły statusów"] },
    { id: 6, title: "Personalizacja Agenta", short: "Personalizacja", tier: "advanced", sections: ["Tożsamość i styl rozmowy", "Inicjatywa sprzedażowa", "Argumenty i obiekcje", "Eskalacja do handlowca"] },
    { id: 7, title: "Wiedza i kwalifikacja", short: "Wiedza", tier: "advanced", sections: ["Informacje o firmie", "Pytania i kryteria kwalifikacji", "Kryteria ze scenariusza"] },
    { id: 8, title: "Follow-up i integracje", short: "Follow-up", tier: "advanced", sections: ["Follow-up", "Systemy docelowe", "Symulacja"] },
];

// One-line orientation per Core step -- what to do here and why, so the user always knows
// the next concrete action instead of having to infer it from the field list.
const STEP_INTROS = {
    1: "Zacznij od nazwania kampanii — to jedyne pole wymagane na tym kroku, reszta jest opcjonalna.",
    2: "Podepnij arkusz, z którego Agent ma pobierać leady do obsłużenia.",
    3: "Dodaj przynajmniej jeden produkt lub usługę — nazwa i typ wystarczą, żeby Agent wiedział, o czym rozmawiać.",
    4: "Określ, co ma być sukcesem rozmowy (np. spotkanie, zakup) — Agent będzie do tego dążył.",
    5: "Wybierz kanały (WhatsApp / SMS / e-mail) i ustaw regułę dla statusu „Nowy” — to ona każe Agentowi zacząć rozmowę.",
    6: "Dostrój ton rozmowy i argumenty sprzedażowe — Agent ma już sensowne wartości domyślne, więc możesz to zrobić w dowolnym momencie.",
    7: "Dodaj wiedzę o firmie i pytania kwalifikujące, żeby Agent lepiej oceniał i odpowiadał leadom.",
    8: "Skonfiguruj follow-up i miejsce zapisu wyników — albo zostaw domyślne ustawienia.",
};

const CampaignFormModal = ({ onClose, initialValues, onSubmit, agentName, scenarioId }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDraftSubmitting, setIsDraftSubmitting] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [visitedSteps, setVisitedSteps] = useState(() => new Set());
    const [warningErrors, setWarningErrors] = useState({});
    const [dismissedIntros, setDismissedIntros] = useState(() => new Set());
    const dismissIntro = (stepId) => setDismissedIntros((prev) => new Set(prev).add(stepId));
    const totalSteps = STEPS.length;
    const activeStep = STEPS[currentStep - 1];
    const isEdit = !!initialValues?.id;

    const formik = useFormik({
        initialValues: initialValues ?? createEmptyCampaign(),
        validationSchema: campaignValidationSchema,
        // Silent until the first failed activation attempt, then live -- mirrors JobOfferFormModal.
        validateOnChange: submitAttempted,
        validateOnBlur: submitAttempted,
        onSubmit: () => {},
    });
    const { values, setValues } = formik;

    const revalidateWarnings = () =>
        validateYupSchema(values, campaignValidationSchema)
            .then(() => setWarningErrors({}))
            .catch((err) => setWarningErrors(yupToFormErrors(err)));

    const goToStep = (stepId) => {
        if (stepId === currentStep) return;
        setVisitedSteps((prev) => new Set(prev).add(currentStep));
        revalidateWarnings();
        setCurrentStep(stepId);
    };

    // Keeps "left incomplete" (yellow) warnings in sync while editing without navigating away --
    // mirrors JobOfferFormModal's debounced re-validation.
    useEffect(() => {
        if (visitedSteps.size === 0) return undefined;
        const handle = setTimeout(revalidateWarnings, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, visitedSteps.size]);

    const save = async (status, setPending) => {
        if (!values.name?.trim()) {
            toaster.create({ type: "error", title: "Nazwa kampanii jest wymagana" });
            setCurrentStep(1);
            return;
        }
        setPending(true);
        try {
            await onSubmit?.({ ...values, status });
        } catch (error) {
            toaster.create({
                type: "error",
                title: extractBusinessErrorMessage(error) ?? "Nie udało się zapisać kampanii",
            });
        } finally {
            setPending(false);
        }
    };

    const handleSaveDraft = () => save("Draft", setIsDraftSubmitting);

    const handleActivate = async () => {
        setSubmitAttempted(true);
        const errors = await formik.validateForm();
        const firstInvalidStep = STEPS.find((s) => stepHasErrors(s.id, errors));
        if (firstInvalidStep) {
            setCurrentStep(firstInvalidStep.id);
            return;
        }
        save("Active", setIsSubmitting);
    };

    const activeStepHasErrors = submitAttempted && stepHasErrors(currentStep, formik.errors);
    const stepNeedsForceOpen = (stepId) =>
        (submitAttempted && stepHasErrors(stepId, formik.errors)) ||
        (visitedSteps.has(stepId) && stepHasErrors(stepId, warningErrors));

    const errorsForReadiness = submitAttempted ? formik.errors : warningErrors;
    const coreStepsReady = !Array.from({ length: CORE_STEP_COUNT }, (_, i) => i + 1)
        .some((id) => stepHasErrors(id, errorsForReadiness));

    const stepProps = { formik, values, setValues, warningErrors, visited: visitedSteps.has(currentStep), forceOpen: stepNeedsForceOpen(currentStep) };

    return (
        <Box position="fixed" inset={0} zIndex={50} display="flex" alignItems="stretch" bg="rgba(0,0,0,0.85)" animation="fadeIn 0.2s ease-out">
            <Box
                position="relative"
                display="flex"
                w="full"
                m={{ base: 0, md: 4 }}
                borderRadius={{ base: 0, md: 16 }}
                overflow="hidden"
                bg="bg.primary"
                maxH={{ base: '100vh', md: 'calc(100vh - 2rem)' }}
                animation="scaleIn 0.25s ease-out"
            >
                {/* Desktop sidebar */}
                <Box
                    flexShrink={0}
                    display={{ base: 'none', md: 'flex' }}
                    flexDirection="column"
                    py={6}
                    overflowY="auto"
                    w="240px"
                    bg="bg.secondary"
                    borderRight="1px solid"
                    borderColor="border.primary"
                >
                    <Box px={5} mb={6}>
                        <Text color="gray.900" fontWeight={600} fontSize="sm" mb={1}>{agentName ?? "SalesManager AI"}</Text>
                        <Text fontSize="xs" color="text.grey">{isEdit ? "Edycja kampanii" : "Nowa kampania"}</Text>
                    </Box>
                    <Box as="nav" display="flex" flexDirection="column" gap={1} px={3}>
                        {STEPS.map((step, index) => {
                            const isActive = currentStep === step.id;
                            const isDone = currentStep > step.id;
                            const hasError = submitAttempted && stepHasErrors(step.id, formik.errors);
                            const hasWarning = !hasError && visitedSteps.has(step.id) && stepHasErrors(step.id, warningErrors);
                            const circle = stepCircleProps(hasError, hasWarning, isDone, isActive);
                            const isFirstAdvanced = step.tier === "advanced" && STEPS[index - 1]?.tier !== "advanced";
                            return (
                                <Box key={step.id}>
                                    {isFirstAdvanced && (
                                        <Text fontSize="10px" fontWeight={700} color="text.grey" textTransform="uppercase" letterSpacing="wider" px={3} pt={4} pb={1}>
                                            Zaawansowane (opcjonalne)
                                        </Text>
                                    )}
                                    <Box
                                        as="button"
                                        type="button"
                                        onClick={() => goToStep(step.id)}
                                        w="full"
                                        display="flex"
                                        alignItems="center"
                                        gap={3}
                                        px={3}
                                        py={2.5}
                                        borderRadius={12}
                                        textAlign="left"
                                        bg={isActive ? "bg.highlight" : "transparent"}
                                        border="1px solid"
                                        borderColor={stepAlertBorderColor(hasError, hasWarning) ?? (isActive ? "border.secondary" : "transparent")}
                                        transition="background 0.2s ease, border-color 0.2s ease"
                                        _hover={{ bg: isActive ? "bg.highlight" : "gray.50" }}
                                    >
                                        <Box
                                            flexShrink={0}
                                            w={6}
                                            h={6}
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontSize="xs"
                                            fontWeight={700}
                                            bg={circle.bg}
                                            color={circle.color}
                                            transition="background 0.2s ease, color 0.2s ease, transform 0.2s ease"
                                            transform={isActive ? "scale(1.08)" : "scale(1)"}
                                        >
                                            {circle.label ?? step.id}
                                        </Box>
                                        <Text fontSize="sm" fontWeight={500} color={stepLabelColor(isActive)} transition="color 0.2s ease">
                                            {step.short}
                                        </Text>
                                    </Box>
                                    {isActive && (
                                        <Box ml={9} mt={1} mb={1} display="flex" flexDirection="column" gap={0.5} animation="fadeSlideIn 0.2s ease-out">
                                            {step.sections.map((sec) => (
                                                <Text key={sec} fontSize="xs" px={2} py={0.5} color="text.grey">{sec}</Text>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Main content */}
                <Box display="flex" flexDirection="column" flex={1} minW={0}>
                    {/* Mobile step bar */}
                    <Box display={{ base: 'flex', md: 'none' }} px={3} py={3} bg="bg.secondary" borderBottom="1px solid" borderColor="border.primary">
                        <HStack justify="space-between" w="full" gap={1}>
                            {STEPS.map((step) => {
                                const hasError = submitAttempted && stepHasErrors(step.id, formik.errors);
                                const hasWarning = !hasError && visitedSteps.has(step.id) && stepHasErrors(step.id, warningErrors);
                                const circle = stepCircleProps(hasError, hasWarning, currentStep > step.id, currentStep === step.id);
                                return (
                                    <Box
                                        key={step.id}
                                        as="button"
                                        type="button"
                                        onClick={() => goToStep(step.id)}
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        gap={1}
                                        flex={1}
                                    >
                                        <Box
                                            w={8} h={8} borderRadius="full" display="flex" alignItems="center" justifyContent="center"
                                            fontSize="xs" fontWeight={700} bg={circle.bg} color={circle.color}
                                            border="2px solid" borderColor={stepAlertBorderColor(hasError, hasWarning) ?? "transparent"}
                                            transition="background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease"
                                            transform={currentStep === step.id ? "scale(1.08)" : "scale(1)"}
                                        >
                                            {circle.label ?? step.id}
                                        </Box>
                                        <Text fontSize="9px" color={stepLabelColor(currentStep === step.id)} textAlign="center" transition="color 0.2s ease">{step.short}</Text>
                                    </Box>
                                );
                            })}
                        </HStack>
                    </Box>

                    {/* Header */}
                    <Box flexShrink={0} display="flex" alignItems="center" justifyContent="space-between" px={{ base: 4, md: 6 }} py={4} bg="bg.primary" borderBottom="1px solid" borderColor="border.primary">
                        <Box minW={0}>
                            <Text color="gray.900" fontSize={{ base: 'sm', md: 'lg' }} fontWeight={700}>{activeStep.title}</Text>
                            <Text fontSize="xs" color="text.grey" mt={0.5}>Krok {currentStep} z {totalSteps}</Text>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                            {activeStepHasErrors && (
                                <Box display={{ base: 'flex', md: 'none' }} w={2} h={2} borderRadius="full" bg="red.500" flexShrink={0} />
                            )}
                            <Box
                                as="button" type="button" onClick={onClose}
                                w={8} h={8} borderRadius={8} display="flex" alignItems="center" justifyContent="center"
                                fontSize="lg" color="text.grey" _hover={{ bg: "bg.highlight" }}
                            >×</Box>
                        </Box>
                    </Box>

                    {/* Step content */}
                    <Box flex={1} overflowY="auto" px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
                        {activeStepHasErrors && (
                            <Box role="alert" mb={4} px={4} py={3} borderRadius={10} bg="red.50" border="1px solid" borderColor="red.200" display="flex" alignItems="flex-start" gap={2.5} animation="fadeSlideIn 0.2s ease-out">
                                <Box as={HiExclamationTriangle} boxSize={5} color="red.500" flexShrink={0} mt="1px" />
                                <Text fontSize="sm" color="red.700" fontWeight={600}>Uzupełnij wymagane pola na tym kroku, żeby aktywować kampanię.</Text>
                            </Box>
                        )}
                        {currentStep === CORE_STEP_COUNT && coreStepsReady ? (
                            <Box role="status" mb={4} px={4} py={3} borderRadius={10} bg="green.50" border="1px solid" borderColor="green.200" display="flex" alignItems="flex-start" gap={2.5} animation="fadeSlideIn 0.2s ease-out">
                                <Box as={HiCheckCircle} boxSize={5} color="green.500" flexShrink={0} mt="1px" />
                                <Text fontSize="sm" color="green.700" fontWeight={600}>
                                    Podstawy kampanii są gotowe. Możesz aktywować teraz, albo przejść dalej i dostroić szczegóły (opcjonalnie).
                                </Text>
                            </Box>
                        ) : (
                            !activeStepHasErrors && STEP_INTROS[currentStep] && !dismissedIntros.has(currentStep) && (
                                <Box role="note" mb={4} px={4} py={3} borderRadius={10} bg="green.50" border="1px solid" borderColor="green.200" display="flex" alignItems="flex-start" gap={2.5} animation="fadeSlideIn 0.2s ease-out">
                                    <Box as={HiLightBulb} boxSize={5} color="green.500" flexShrink={0} mt="1px" />
                                    <Text fontSize="sm" color="green.700" flex={1}>{STEP_INTROS[currentStep]}</Text>
                                    <Box
                                        as="button" type="button" onClick={() => dismissIntro(currentStep)}
                                        aria-label="Zamknij podpowiedź" flexShrink={0} display="flex" color="green.500" _hover={{ color: "green.700" }}
                                        transition="color 0.15s ease"
                                    >
                                        <HiXMark size={16} />
                                    </Box>
                                </Box>
                            )
                        )}
                        <Box key={currentStep} animation="fadeSlideIn 0.2s ease-out">
                            {currentStep === 1 && <Step1Campaign {...stepProps} />}
                            {currentStep === 2 && <Step2LeadSource {...stepProps} />}
                            {currentStep === 3 && <Step3Products {...stepProps} />}
                            {currentStep === 4 && <Step4ConversionGoal {...stepProps} />}
                            {currentStep === 5 && <Step5ChannelsAndRules {...stepProps} />}
                            {currentStep === 6 && <Step6AgentPersonalization {...stepProps} />}
                            {currentStep === 7 && <Step7KnowledgeQualifying {...stepProps} campaignId={values.id} scenarioId={scenarioId} />}
                            {currentStep === 8 && <Step8FollowUpOutput {...stepProps} />}
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box flexShrink={0} display="flex" alignItems="center" justifyContent="space-between" px={{ base: 4, md: 6 }} py={4} bg="bg.primary" borderTop="1px solid" borderColor="border.primary" gap={2}>
                        <Box
                            as="button" type="button"
                            onClick={() => goToStep(Math.max(1, currentStep - 1))}
                            disabled={currentStep === 1}
                            px={4} py={2} borderRadius={8} fontSize="sm"
                            bg="bg.highlight" color="text.white" border="1px solid" borderColor="border.secondary"
                            opacity={currentStep === 1 ? 0.4 : 1}
                            cursor={currentStep === 1 ? "not-allowed" : "pointer"}
                            transition="opacity 0.15s ease, transform 0.1s ease, background 0.15s ease"
                            _hover={currentStep === 1 ? undefined : { bg: "gray.200" }}
                            _active={currentStep === 1 ? undefined : { transform: "scale(0.97)" }}
                        >
                            Wstecz
                        </Box>
                        <HStack gap={2}>
                            <Box
                                as="button" type="button"
                                onClick={handleSaveDraft}
                                disabled={isDraftSubmitting || isSubmitting}
                                px={4} py={2} borderRadius={8} fontSize="sm"
                                bg="bg.highlight" color="text.white" border="1px solid" borderColor="border.secondary"
                                opacity={isDraftSubmitting || isSubmitting ? 0.6 : 1}
                                display="flex" alignItems="center" gap={2}
                                transition="opacity 0.15s ease, transform 0.1s ease, background 0.15s ease"
                                _hover={{ bg: "gray.200" }}
                                _active={{ transform: "scale(0.97)" }}
                            >
                                {isDraftSubmitting && <Spinner size="xs" />}
                                Zapisz jako szkic
                            </Box>
                            {currentStep < totalSteps && (
                                <Box
                                    as="button" type="button"
                                    onClick={() => goToStep(Math.min(totalSteps, currentStep + 1))}
                                    px={4} py={2} borderRadius={8} fontSize="sm"
                                    transition="opacity 0.15s ease, transform 0.1s ease, filter 0.15s ease"
                                    _hover={{ filter: "brightness(1.08)" }}
                                    _active={{ transform: "scale(0.97)" }}
                                    {...(currentStep >= CORE_STEP_COUNT
                                        ? { bg: "bg.highlight", color: "text.white", border: "1px solid", borderColor: "border.secondary" }
                                        : { bg: "purple.500", color: "white" })}
                                >
                                    Dalej
                                </Box>
                            )}
                            {currentStep >= CORE_STEP_COUNT && (
                                <Box
                                    as="button" type="button"
                                    onClick={handleActivate}
                                    disabled={isSubmitting || isDraftSubmitting}
                                    px={4} py={2} borderRadius={8} fontSize="sm"
                                    bg="purple.500" color="white"
                                    opacity={isSubmitting || isDraftSubmitting ? 0.6 : 1}
                                    display="flex" alignItems="center" gap={2}
                                    transition="opacity 0.15s ease, transform 0.1s ease, filter 0.15s ease"
                                    _hover={{ filter: "brightness(1.08)" }}
                                    _active={{ transform: "scale(0.97)" }}
                                >
                                    {isSubmitting && <Spinner size="xs" />}
                                    Zapisz i aktywuj
                                </Box>
                            )}
                        </HStack>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default CampaignFormModal;
